// ─── Shark Tank India Season 6 · Young Founders Special ──────────────────────
// CoGrad is an outreach partner, not an organiser. That shapes everything here:
//
//   • The official poster is the only show creative we use. We do not redraw it,
//     crop it, or rebuild the show's logos as separate artwork of our own.
//   • Every fact below comes from the official FAQ document SPNI published on
//     12 October 2026 (prd-emss3.sonyliv.com → "STI6 Young Founders Special
//     FAQs"), re-worded in our own voice. Never copy their sentences verbatim —
//     both for copyright and because duplicate text does not rank. If anything
//     here disagrees with that document, the document wins.
//   • Registration happens on the official Shark Tank site. CoGrad does not
//     shortlist, select or charge anybody. The page carried a footer line
//     saying so; the founder had it removed on 24 Oct 2026, so the only place
//     that now states it is the "What is CoGrad's role in this?" FAQ. Keep
//     that answer — it is the last thing separating this page from one that
//     reads as official.
//
// ── On the two dates ─────────────────────────────────────────────────────────
// The official FAQ says registrations run 12 Oct – 11 Nov 2026. Our poster says
// entries close on 30 October, and the founder chose to run the page on that
// date (24 Oct 2026). The two have not been reconciled with the organisers.
// So the page says "entries close 30 October" — the poster's own wording — and
// never claims that is when official registrations close, because that would
// state as fact something the official document contradicts. If the organisers
// confirm 11 November, change `closesLabel`/`closesAt` below and the row in
// `details`; nothing else needs touching.

export const youngFounders = {
  registerUrl: "https://sharktank.sonyliv.com/youngfounder/cograd",
  path: "/sharktank-youngfounder",
  closesAt: "2026-10-30T23:59:59+05:30",
  closesLabel: "30 October",
  eligibility: "under 15",
  officialWindow: { opensLabel: "12 October 2026", closesLabel: "11 November 2026" },

  // The document every fact on this page is drawn from. Linking it lets a
  // parent check us rather than take our word for it.
  officialFaq: {
    url: "https://prd-emss3.sonyliv.com/Creatives/ST/STI6%20Young%20Founders%20Special%20FAQs_12.10.2026_final.pdf",
    label: "official Shark Tank India Season 6 Young Founders Special FAQs",
    dated: "12 October 2026",
  },
};

// The page's title, description, canonical and OG image live in a JSON file
// because two different things need them: this page (through react-helmet, in
// the browser) and scripts/prerender-meta.js (in Node, at build time). Helmet
// only runs once JavaScript has executed, and WhatsApp, LinkedIn and X never
// execute it — so the build step writes the same tags into the served HTML.
// Keeping one file means the two copies can never drift apart.
// The OG image is served from /public so scrapers get a stable absolute URL.
export { default as seo } from "./youngFounders.seo.json";

export const hero = {
  eyebrow: "Shark Tank India · Season 6",
  // The poster says this too, but as pixels — nothing can read a JPEG. These
  // are the same promises in text, for Google and for screen readers.
  h1: ["Got a business idea?", "Pitch it to the Sharks."],
  // `short` is what a phone shows: the full wording needs two lines there,
  // and three promises on one line beat three and a half on two.
  chips: [
    { long: "Entries open till 30 October", short: "Till 30 Oct" },
    { long: "Ages 6–15", short: "Ages 6–15" },
    { long: "Free to apply", short: "Free" },
  ],
  cta: "Apply now",
  secondary: "See if you qualify",
  posterAlt:
    "Shark Tank India Young Founders Special — got a business idea? Entries are open till 30th October. Eligibility: under 15 years old.",
};

/**
 * The strip under the hero: four things worth knowing before reading on.
 * Each tile carries an icon, because a bare block of colour tells a reader
 * nothing — `icon` names a lucide-react component, resolved in the page.
 */
export const facts = [
  { value: "6–15", label: "Years old", icon: "users", accent: "blue" },
  { value: "Free", label: "To apply", icon: "rupee", accent: "yellow" },
  { value: "An idea", label: "Is all you need", icon: "bulb", accent: "green" },
  { value: "4", label: "Rounds to the Sharks", icon: "steps", accent: "violet" },
];

export const about = {
  label: "About the initiative",
  title: ["What is the", "Young Founders Special?"],
  body: [
    "Season 6 of Shark Tank India has opened a round built for children, not adults. A kid with a business idea gets to pitch it on national television — to the same Sharks the show is known for.",
    "You do not need a company. You do not need sales. You do not even need a finished product. An idea you can explain is enough, and applying costs nothing at all.",
  ],
  // Four mini cards beside the copy. `icon` names a lucide-react component.
  side: [
    { k: "Who", v: "Children aged 6 to 15", icon: "users", accent: "blue" },
    { k: "Stage", v: "An idea is enough", icon: "bulb", accent: "green" },
    { k: "Fee", v: "Free, start to finish", icon: "rupee", accent: "violet" },
    { k: "Applies", v: "A parent or guardian", icon: "shield", accent: "pink" },
  ],
};

// `icon` names a lucide-react component and `accent` a colour; both are
// resolved in the page, because data files must not import components.
export const eligibility = {
  label: "Eligibility",
  title: ["Who can", "apply?"],
  lead: "Five things decide it, and all five come from the official FAQ. Most people are put off by the ones that do not actually apply.",
  cards: [
    {
      n: "01",
      tone: 0,
      icon: "cake",
      accent: "blue",
      head: "Age",
      body: "Between 6 and 15 years, counted as on 12 October 2026 — not the day you fill the form.",
    },
    {
      n: "02",
      tone: 1,
      icon: "flag",
      accent: "espresso",
      head: "Citizenship",
      body: "You have to be an Indian citizen, of sound mind and health.",
    },
    {
      n: "03",
      tone: 2,
      icon: "school",
      accent: "green",
      head: "School status",
      body: "In school, home-schooled, or not enrolled anywhere right now — all three are fine.",
    },
    {
      n: "04",
      tone: 3,
      icon: "lightbulb",
      accent: "violet",
      head: "Business stage",
      body: "An idea on its own counts. No registered company, no finished product, no revenue needed.",
    },
    {
      n: "05",
      tone: 4,
      icon: "users",
      accent: "pink",
      head: "Parent or guardian",
      body: "You are a minor, so a parent or legal guardian fills the form and stays the point of contact.",
    },
  ],
};

// No imagery here on purpose: the illustrations this section once used were
// drawn for the YUVASETU hackathon and had nothing to do with Shark Tank, so
// they read as borrowed. Until there is artwork made for this campaign the
// rounds are numbered cards — which looks deliberate rather than unfinished.
export const steps = {
  label: "The path",
  title: ["Four rounds between you", "and the Sharks"],
  lead: "Each one is cleared before the next begins. Nothing after the form has to be ready today.",
  items: [
    {
      n: "01",
      icon: "form",
      head: "The form",
      body: "A parent or guardian fills in your details and your idea. Photos of the product go here, if you have made one.",
      tag: "Online",
    },
    {
      n: "02",
      icon: "video",
      head: "A video call",
      body: "A proper conversation about the idea. This is where your 90-second pitch video and demo are asked for.",
      tag: "Video",
    },
    {
      n: "03",
      icon: "pin",
      head: "Audition in Mumbai",
      body: "In person, on a date the makers give you. A parent or guardian has to come along.",
      tag: "In person",
    },
    {
      n: "04",
      icon: "panel",
      head: "The panel",
      body: "A panel watches your audition footage and goes through the business details you have sent.",
      tag: "Review",
    },
  ],
};

export const details = {
  label: "Important info",
  title: ["Dates &", "details"],
  // `icon` names a lucide-react component and `accent` a colour; both are
  // resolved in the page, because data files must not import components.
  rows: [
    { k: "Initiative", v: "Shark Tank India Season 6 — Young Founders Special", icon: "tv", accent: "yellow" },
    { k: "Eligible age", v: "6 to 15 years, as on 12 October 2026", icon: "users", accent: "blue" },
    { k: "Citizenship", v: "Indian", icon: "flag", accent: "pink" },
    { k: "Entries close", v: "30 October", icon: "calendar", accent: "green" },
    { k: "Selection period", v: "12 October – 23 December 2026", icon: "calendarRange", accent: "violet" },
    { k: "Application fee", v: "Free", icon: "rupee", accent: "yellow" },
    { k: "Where to register", v: "On the official Shark Tank site", icon: "globe", accent: "blue", link: true },
    { k: "Auditions", v: "In person, in Mumbai", icon: "pin", accent: "pink" },
  ],
  noteLabel: "Important",
  note: "Dates and selection timelines can change at the organisers' discretion. Always check the official registration page before you apply, and apply early rather than close to the deadline.",
};

export const register = {
  label: "Register",
  title: ["Where to", "register"],
  body: "Applications are submitted online, on the official Shark Tank site, by a parent or legal guardian. There is no application fee at any stage — anyone asking a family for money in the name of the show is committing a fraud.",
};

export const faqTitle = ["Common", "questions"];
export const faqNote =
  "Every answer below is taken from the official Shark Tank India Season 6 Young Founders Special FAQs, dated 12 October 2026, and written in our own words.";

export const faqs = [
  {
    q: "Who can apply to the Young Founders Special?",
    a: "Any Indian citizen aged between 6 and 15 as on 12 October 2026, of sound mind and health. Children enrolled in a school, home-schooled children and children not in school at all are all eligible — there is no requirement to be studying anywhere.",
  },
  {
    q: "Does my child need an actual business to apply?",
    a: "No. An idea is enough. There is no need for a registered company, a finished product or any revenue — pre-revenue and idea-stage applications are welcome.",
  },
  {
    q: "Is there any registration or application fee?",
    a: "No. The process is free from the form through to the audition. Anyone asking a family for money in the name of the show is committing a fraud and should be reported.",
  },
  {
    q: "Who fills the registration form — the child or the parent?",
    a: "A parent or legal guardian, because the applicant is a minor. The mobile number on the form must belong to that parent or guardian, since every update about the application goes to that number.",
  },
  {
    q: "Can we submit more than one business idea?",
    a: "Yes. Each idea has to be submitted on a different mobile number, and each of those numbers must be registered in the parent's or legal guardian's name.",
  },
  {
    q: "What happens after we submit the form?",
    a: "There are four rounds: the application form, a detailed Q&A over video call, an in-person audition in Mumbai, and an executive pitching round where a panel reviews the audition footage and the business details. Children who clear all four pitch to the Sharks in the studio.",
  },
  {
    q: "What will we have to submit along the way?",
    a: "Photographs of the product with the form, if one exists. For the online round: a pitch video of at most 90 seconds, a prototype or product demonstration video, a written business plan if there is one, ID and address proof for KYC, any trademark or patent certificates, and a safety testing certificate for prototypes and products already selling. Videos as .mp4, .mov or .avi up to 1GB, documents as .pdf or .ppt up to 100MB, everything in Hindi or English.",
  },
  {
    q: "Where are the auditions held?",
    a: "In Mumbai, on dates and time slots the makers communicate to shortlisted applicants. A parent or legal guardian has to accompany the child at every stage of the audition and the shoot.",
  },
  {
    q: "Does my child need to know Hindi?",
    a: "No. Children are encouraged to pitch in whichever language they are most comfortable in. Hindi and English are preferred so the Sharks can follow along, but nobody is selected or rejected on the basis of language.",
  },
  {
    q: "Which language can the form be filled in?",
    a: "English or Hindi. The choice cannot be changed once the form has been started, so pick before beginning.",
  },
  {
    q: "How will we know if the child has been selected?",
    a: "The parent or legal guardian is contacted by email or phone. Mails from SONYLIV can land in the spam or junk folder, so it is worth checking there through the process. Because of the volume of applications, families who are not selected may not hear back at all.",
  },
  {
    q: "We made a mistake in the form. Can it be corrected?",
    a: "Either apply again using a different mobile number, or write to livsupport@sonyliv.com explaining the error.",
  },
  {
    q: "What can a child win if the Sharks like the pitch?",
    a: "A grant may be offered by the Sharks through a trust managed by the registered parent or legal guardian, along with mentorship and an investment meeting once the child turns 18. The official terms and conditions on sonyliv.com set out exactly how this works.",
  },
  {
    q: "Are the documents we upload kept confidential?",
    a: "Yes. They are used only by SPNI and the Sharks to evaluate the application.",
  },
  {
    q: "What is CoGrad's role in this?",
    a: "CoGrad is an outreach partner, which means we help carry the call for entries to schools and families across India. Registration itself happens on the official Shark Tank website. CoGrad does not shortlist, select or charge anyone.",
  },
];

/**
 * "open" until entries close, then "closed". The home-page popup reverts to
 * the evergreen incubation pitch on its own, so nobody has to remember to
 * take the campaign down on the 31st. `?yf=open` forces it back for a check.
 */
export function youngFoundersState(now = Date.now()) {
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("yf");
    if (q === "open" || q === "closed") return q;
  }
  return now > new Date(youngFounders.closesAt).getTime() ? "closed" : "open";
}
